import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { websiteScanApi } from '../lib/api';
import { useFeatureAccess } from '../hooks/useFeatureAccess';
import {
  GlobeAltIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  XCircleIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';

function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { bg: string; text: string; icon: any; label: string }> = {
    PENDING: { bg: 'bg-gray-100', text: 'text-gray-700', icon: ClockIcon, label: 'Pending' },
    CRAWLING: { bg: 'bg-blue-100', text: 'text-blue-700', icon: ArrowPathIcon, label: 'Crawling' },
    ANALYZING: { bg: 'bg-amber-100', text: 'text-amber-700', icon: ArrowPathIcon, label: 'Analyzing' },
    COMPLETED: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircleIcon, label: 'Completed' },
    ERROR: { bg: 'bg-red-100', text: 'text-red-700', icon: XCircleIcon, label: 'Error' },
  };

  const c = config[status] || config.PENDING;
  const Icon = c.icon;

  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${c.bg} ${c.text}`}>
      <Icon className={`h-3.5 w-3.5 ${status === 'CRAWLING' || status === 'ANALYZING' ? 'animate-spin' : ''}`} />
      {c.label}
    </span>
  );
}

export default function WebsiteScanPage() {
  const navigate = useNavigate();
  const { hasFeature } = useFeatureAccess();
  const hasAccess = hasFeature('website_compliance');

  const [showForm, setShowForm] = useState(false);
  const [url, setUrl] = useState('');
  const [crawlDepth, setCrawlDepth] = useState(2);
  const [maxPages, setMaxPages] = useState(25);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['website-scans'],
    queryFn: async () => {
      const response = await websiteScanApi.listScans();
      return response.data;
    },
    enabled: hasAccess,
    refetchInterval: 10000, // Poll for updates every 10s
  });

  const startScanMutation = useMutation({
    mutationFn: (data: { url: string; crawlDepth: number; maxPages: number }) =>
      websiteScanApi.startScan(data),
    onSuccess: (response) => {
      toast.success('Website scan started');
      setShowForm(false);
      setUrl('');
      refetch();
      navigate(`/website-scan/${response.data.id}`);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to start scan');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    let normalizedUrl = url.trim();
    if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
      normalizedUrl = 'https://' + normalizedUrl;
    }
    startScanMutation.mutate({ url: normalizedUrl, crawlDepth, maxPages });
  };

  // Upgrade prompt for users without website_compliance feature
  if (!hasAccess) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md">
          <GlobeAltIcon className="mx-auto h-16 w-16 text-gray-300" />
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Website Compliance Scanning</h2>
          <p className="mt-2 text-gray-600">
            Automatically crawl and check your institution's website for FDIC, NCUA, and UDAAP compliance issues.
          </p>
          <p className="mt-4 text-sm text-gray-500">
            This feature is available on Professional and Enterprise plans.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-block rounded-lg bg-teal-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-teal-700 transition-colors"
          >
            Contact Sales
          </Link>
        </div>
      </div>
    );
  }

  const scans = data?.scans || [];

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Website Compliance</h1>
          <p className="text-sm text-gray-600 mt-1">
            Scan your institution's website for regulatory compliance issues
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-teal-700 transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          New Scan
        </button>
      </div>

      {/* New Scan Form */}
      {showForm && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Start a Website Scan</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website URL
              </label>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.yourbank.com"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Crawl Depth (1-3)
                </label>
                <input
                  type="range"
                  min="1"
                  max="3"
                  value={crawlDepth}
                  onChange={(e) => setCrawlDepth(Number(e.target.value))}
                  className="w-full accent-teal-600"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                  <span>1 (Homepage only)</span>
                  <span>2 (Recommended)</span>
                  <span>3 (Deep)</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Pages
                </label>
                <input
                  type="number"
                  min="1"
                  max="50"
                  value={maxPages}
                  onChange={(e) => setMaxPages(Number(e.target.value))}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none"
                />
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={startScanMutation.isPending}
                className="rounded-lg bg-teal-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-teal-700 transition-colors disabled:opacity-50"
              >
                {startScanMutation.isPending ? 'Starting...' : 'Start Scan'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="rounded-lg border border-gray-300 px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Scans List */}
      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading scans...</div>
      ) : scans.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <GlobeAltIcon className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">No website scans yet</h3>
          <p className="mt-2 text-sm text-gray-500">
            Start your first scan to check your website for compliance issues.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-teal-700 transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            New Scan
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Website
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pages
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Issues
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Started
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {scans.map((scan: any) => {
                const results = scan.overallResults as any;
                return (
                  <tr
                    key={scan.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/website-scan/${scan.id}`)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                        <div>
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[300px]">
                            {scan.url}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={scan.status} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {scan.pagesAnalyzed}/{scan.pagesDiscovered}
                    </td>
                    <td className="px-6 py-4">
                      {results?.totalViolations != null ? (
                        <span className={`text-sm font-medium ${results.totalViolations > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {results.totalViolations > 0 ? (
                            <span className="flex items-center gap-1">
                              <ExclamationTriangleIcon className="h-4 w-4" />
                              {results.totalViolations}
                            </span>
                          ) : (
                            <span className="flex items-center gap-1">
                              <CheckCircleIcon className="h-4 w-4" />
                              Clean
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400">--</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDistanceToNow(new Date(scan.createdAt), { addSuffix: true })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
